import { createId } from "@paralleldrive/cuid2";
import { MiniGameParser } from "../pgn/MiniGameParser";
import { PgnParser } from "../pgn/PgnParser";
export var TopicDifficulty;
(function (TopicDifficulty) {
    TopicDifficulty["Beginner"] = "Beginner";
    TopicDifficulty["Rookie"] = "Rookie";
    TopicDifficulty["Intermediate"] = "Intermediate";
    TopicDifficulty["Advanced"] = "Advanced";
    TopicDifficulty["Mixed"] = "Mixed";
})(TopicDifficulty || (TopicDifficulty = {}));
export var ContentStatus;
(function (ContentStatus) {
    ContentStatus["Draft"] = "Draft";
    ContentStatus["Published"] = "Published";
    ContentStatus["Archived"] = "Archived";
})(ContentStatus || (ContentStatus = {}));
export class Topic {
    id;
    title;
    description;
    difficultyLevel;
    status;
    isPublic;
    ownerId;
    owner;
    tags;
    urls;
    workbookRef;
    originalTopicId;
    assignmentRefs; // References to assignments that use this topic
    // Scenario counts metadata
    exampleCount;
    puzzleCount;
    classworkCount;
    homeworkCount;
    totalScenarios;
    scenariosUpdatedAt;
    constructor(data) {
        this.id = createId();
        this.title = data.title;
        this.description = data.description;
        this.difficultyLevel = data.difficultyLevel;
        this.status = data.status || ContentStatus.Draft;
        this.isPublic = data.isPublic;
        this.ownerId = data.ownerId;
        this.owner = data.owner;
        this.tags = data.tags || [];
        this.urls = data.urls || [];
        this.workbookRef = data.workbookRef;
        this.originalTopicId = data.originalTopicId;
        this.assignmentRefs = data.assignmentRefs || [];
        this.exampleCount = data.exampleCount;
        this.puzzleCount = data.puzzleCount;
        this.classworkCount = data.classworkCount;
        this.homeworkCount = data.homeworkCount;
        this.totalScenarios = data.totalScenarios;
        this.scenariosUpdatedAt = data.scenariosUpdatedAt;
    }
    update(data) {
        this.title = data.title || this.title;
        this.description = data.description || this.description;
        this.difficultyLevel = data.difficultyLevel || this.difficultyLevel;
        this.status = data.status || this.status;
        if (data.tags)
            this.tags = data.tags;
        if (data.urls)
            this.urls = data.urls;
        if (data.workbookRef !== undefined)
            this.workbookRef = data.workbookRef;
        // Update scenario counts if provided
        if (data.exampleCount !== undefined)
            this.exampleCount = data.exampleCount;
        if (data.puzzleCount !== undefined)
            this.puzzleCount = data.puzzleCount;
        if (data.classworkCount !== undefined)
            this.classworkCount = data.classworkCount;
        if (data.homeworkCount !== undefined)
            this.homeworkCount = data.homeworkCount;
        if (data.totalScenarios !== undefined)
            this.totalScenarios = data.totalScenarios;
        if (data.scenariosUpdatedAt !== undefined)
            this.scenariosUpdatedAt = data.scenariosUpdatedAt;
        // isPublic, ownerId, and assignmentRefs can't be changed through update
    }
    /**
     * Returns true if this topic is locked (has active assignments)
     */
    get isLocked() {
        return this.assignmentRefs.length > 0;
    }
    /**
     * Calculate scenario counts from a list of scenarios
     */
    static calculateScenarioCounts(scenarios) {
        const counts = {
            exampleCount: 0,
            puzzleCount: 0,
            classworkCount: 0,
            homeworkCount: 0,
            totalScenarios: scenarios.length,
        };
        for (const scenario of scenarios) {
            // Count by type
            if (scenario.isPuzzle) {
                counts.puzzleCount++;
            }
            else {
                counts.exampleCount++;
            }
            // Count by homework status
            if (scenario.isHomework) {
                counts.homeworkCount++;
            }
            else {
                counts.classworkCount++;
            }
        }
        return counts;
    }
    /**
     * Check if scenario counts need to be updated
     */
    needsCountUpdate(scenarios) {
        // If counts are missing, need update
        if (this.totalScenarios === undefined) {
            return true;
        }
        // If total count doesn't match, need update
        if (this.totalScenarios !== scenarios.length) {
            return true;
        }
        // Calculate current counts and compare
        const currentCounts = Topic.calculateScenarioCounts(scenarios);
        return (this.exampleCount !== currentCounts.exampleCount ||
            this.puzzleCount !== currentCounts.puzzleCount ||
            this.classworkCount !== currentCounts.classworkCount ||
            this.homeworkCount !== currentCounts.homeworkCount);
    }
}
export class Scenario {
    static ORDER_INTERVAL = 10;
    static NORMALIZE_THRESHOLD = 2;
    id;
    topicId;
    topicTitle; // Optional topic title for display purposes
    title;
    isPuzzle;
    isHomework;
    /** Mini-game configuration. If present with a handler, this scenario is a mini-game. */
    miniGameConfig;
    pgn;
    status;
    difficultyLevel;
    order;
    puzzleLevel;
    createdAt;
    updatedAt;
    previewFen;
    previewLastMove;
    moveCount;
    hasValidationErrors;
    fingerprint;
    constructor(topicId, data) {
        const normalizedPgn = PgnParser.normalize(data.pgn);
        const headers = PgnParser.extractHeaders(normalizedPgn);
        this.id = createId();
        this.topicId = topicId;
        this.title = headers.get("Event") || "Untitled Example";
        this.isPuzzle = PgnParser.isPuzzle(normalizedPgn);
        this.isHomework = headers.get("Homework") === "1";
        // Mini-game config: extracted from [MiniGame "..."] PGN header (single source of truth)
        this.miniGameConfig =
            MiniGameParser.extractConfigData(normalizedPgn) ?? undefined;
        this.pgn = normalizedPgn;
        this.status = data.status || ContentStatus.Published;
        this.difficultyLevel = data.difficultyLevel;
        this.order = data.order ?? 0;
        this.puzzleLevel = PgnParser.parsePuzzleLevel(headers.get("PuzzleLevel"));
        this.createdAt = data.createdAt || new Date();
        this.updatedAt = data.updatedAt || new Date();
        this.previewFen = data.previewFen;
        this.previewLastMove = data.previewLastMove;
        this.moveCount = data.moveCount;
        this.hasValidationErrors = data.hasValidationErrors;
        this.fingerprint = data.fingerprint;
    }
    update(data) {
        const normalizedPgn = PgnParser.normalize(data.pgn);
        const headers = PgnParser.extractHeaders(normalizedPgn);
        this.title = headers.get("Event") || this.title;
        this.isPuzzle = PgnParser.isPuzzle(normalizedPgn);
        this.isHomework = headers.get("Homework") === "1";
        // Mini-game config: extracted from [MiniGame "..."] PGN header (single source of truth)
        this.miniGameConfig =
            MiniGameParser.extractConfigData(normalizedPgn) ?? undefined;
        this.pgn = normalizedPgn;
        this.puzzleLevel = PgnParser.parsePuzzleLevel(headers.get("PuzzleLevel"));
        if (data.order !== undefined) {
            this.order = data.order;
        }
        if (data.status !== undefined) {
            this.status = data.status;
        }
        if (data.difficultyLevel !== undefined) {
            this.difficultyLevel = data.difficultyLevel;
        }
        if (data.previewFen !== undefined) {
            this.previewFen = data.previewFen;
        }
        if (data.previewLastMove !== undefined) {
            this.previewLastMove = data.previewLastMove;
        }
        if (data.moveCount !== undefined) {
            this.moveCount = data.moveCount;
        }
        if (data.hasValidationErrors !== undefined) {
            this.hasValidationErrors = data.hasValidationErrors;
        }
        if (data.fingerprint !== undefined) {
            this.fingerprint = data.fingerprint;
        }
        this.updatedAt = new Date();
    }
    static getNextOrder(scenarios) {
        if (!scenarios.length)
            return Scenario.ORDER_INTERVAL;
        return Math.max(...scenarios.map((s) => s.order)) + Scenario.ORDER_INTERVAL;
    }
    static getSequentialOrders(existingScenarios, count) {
        const startingOrder = Scenario.getNextOrder(existingScenarios);
        return Array.from({ length: count }, (_, index) => startingOrder + index * Scenario.ORDER_INTERVAL);
    }
    static getOrderBetween(beforeOrder, afterOrder) {
        return Math.floor((beforeOrder + afterOrder) / 2);
    }
    static shouldNormalize(scenarios) {
        if (scenarios.length < 2)
            return false;
        const orderedScenarios = [...scenarios].sort((a, b) => a.order - b.order);
        for (let i = 1; i < orderedScenarios.length; i++) {
            const gap = orderedScenarios[i].order - orderedScenarios[i - 1].order;
            if (gap < Scenario.NORMALIZE_THRESHOLD)
                return true;
        }
        return false;
    }
    static normalizeOrders(scenarios) {
        const orderedScenarios = [...scenarios].sort((a, b) => a.order - b.order);
        return orderedScenarios.map((scenario, index) => {
            const normalizedScenario = new Scenario(scenario.topicId, {
                pgn: scenario.pgn,
                order: (index + 1) * Scenario.ORDER_INTERVAL,
                status: scenario.status,
                difficultyLevel: scenario.difficultyLevel,
                previewFen: scenario.previewFen,
                previewLastMove: scenario.previewLastMove,
                moveCount: scenario.moveCount,
                hasValidationErrors: scenario.hasValidationErrors,
            });
            normalizedScenario.id = scenario.id; // Preserve the original ID
            return normalizedScenario;
        });
    }
    static reorder(scenarios, fromIndex, toIndex) {
        const orderedScenarios = [...scenarios].sort((a, b) => a.order - b.order);
        const [movedScenario] = orderedScenarios.splice(fromIndex, 1);
        orderedScenarios.splice(toIndex, 0, movedScenario);
        // Calculate new order for the moved item
        const newOrder = (() => {
            if (toIndex === 0) {
                // Moving to start - use half of first remaining item's order
                return orderedScenarios[1].order / 2;
            }
            else if (toIndex === orderedScenarios.length) {
                // Moving to end - add interval to last item's order
                return orderedScenarios[toIndex - 1].order + Scenario.ORDER_INTERVAL;
            }
            else {
                // Moving between items - use middle value
                return Scenario.getOrderBetween(orderedScenarios[toIndex - 1].order, orderedScenarios[toIndex + 1].order);
            }
        })();
        movedScenario.order = newOrder;
        // Check if we need to normalize
        if (Scenario.shouldNormalize(orderedScenarios)) {
            return Scenario.normalizeOrders(orderedScenarios);
        }
        return orderedScenarios;
    }
}
